import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Button,
} from "@mui/material";
import {
  People,
  School,
  Assignment,
  Assessment,
  TrendingUp,
  MoreVert,
  ArrowForward,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
);

const GOLD = "#D4AF37";

const Dashboard = () => {
  const theme = useTheme();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    enrolledUsers: 0,
    pendingUsers: 0,
  });

  const fetchingRef = React.useRef(false);

  useEffect(() => {
    if (!token || fetchingRef.current) return;
    const fetchData = async () => {
      fetchingRef.current = true;
      try {
        setLoading(true);
        const response = await axios.get(
          "https://tdtlworld.com/dss-kaas-backend/lms/reports/students/json/",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = response.data;
        setUsers(data);

        const totalUsers = data.length;
        const activeUsers = data.filter(
          (user) =>
            user.courses_enrolled && !user.courses_enrolled.includes("None"),
        ).length;
        const enrolledUsers = data.filter(
          (user) =>
            user.courses_enrolled &&
            user.courses_enrolled.length > 0 &&
            !user.courses_enrolled.includes("None"),
        ).length;
        const pendingUsers = data.filter(
          (user) =>
            user.courses_enrolled && user.courses_enrolled.includes("None"),
        ).length;

        setStats({ totalUsers, activeUsers, enrolledUsers, pendingUsers });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };
    fetchData();
  }, [token]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        grid: { color: alpha("#000", 0.05) },
        ticks: { font: { family: "Outfit" } },
      },
      x: { grid: { display: false }, ticks: { font: { family: "Outfit" } } },
    },
  };

  const lineChartData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    datasets: [
      {
        label: "Growth",
        data: [30, 45, 40, 60, 55, 80, 75],
        borderColor: GOLD,
        backgroundColor: alpha(GOLD, 0.1),
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointBackgroundColor: GOLD,
      },
    ],
  };

  const barChartData = {
    labels: ["Python", "JS", "MUI", "React", "Node"],
    datasets: [
      {
        label: "Enrollments",
        data: [120, 190, 150, 250, 170],
        backgroundColor: [
          GOLD,
          alpha(GOLD, 0.8),
          alpha(GOLD, 0.6),
          alpha(GOLD, 0.4),
          alpha(GOLD, 0.2),
        ],
        borderRadius: 8,
      },
    ],
  };

  const StatCard = ({ title, value, icon, trend }) => (
    <Card sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: "12px",
              bgcolor: alpha(GOLD, 0.1),
              color: GOLD,
            }}
          >
            {icon}
          </Box>
          <IconButton size="small">
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 600, mb: 0.5 }}
        >
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          {value}
        </Typography>
        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TrendingUp sx={{ color: "#4caf50", fontSize: 16 }} />
            <Typography
              variant="caption"
              sx={{ color: "#4caf50", fontWeight: 700 }}
            >
              +{trend}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 0.5,
              color: theme.palette.mode === "dark" ? "#FFFFFF" : "text.primary",
            }}
          >
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back to the Admin control center.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<ArrowForward />}
          sx={{ borderRadius: "50px" }}
        >
          Generate Report
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={stats.totalUsers}
            icon={<People />}
            trend="12"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Courses"
            value={stats.activeUsers}
            icon={<School />}
            trend="8"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Enrollments"
            value={stats.enrolledUsers}
            icon={<Assignment />}
            trend="15"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Verifications"
            value={stats.pendingUsers}
            icon={<Assessment />}
            trend="3"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 3 }}>
                Enrollment Growth
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line data={lineChartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 3 }}>
                Popular Courses
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={barChartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={800}>
              Recent Students
            </Typography>
            <Button size="small" sx={{ color: GOLD }}>
              View All
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: alpha(GOLD, 0.05) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: GOLD }}>
                    Student
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: GOLD }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: GOLD }}>
                    Joined Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: GOLD }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.slice(0, 5).map((user, idx) => {
                  const isActive =
                    user.courses_enrolled &&
                    !user.courses_enrolled.includes("None");
                  return (
                    <TableRow
                      key={idx}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: GOLD,
                              fontWeight: 700,
                              fontSize: "0.8rem",
                            }}
                          >
                            {user.full_name?.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={700}>
                            {user.full_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {user.email}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {user.join_date?.split(" ")[0]}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? "Active" : "Pending"}
                          size="small"
                          sx={{
                            bgcolor: isActive
                              ? alpha("#4caf50", 0.1)
                              : alpha("#ff9800", 0.1),
                            color: isActive ? "#4caf50" : "#ff9800",
                            fontWeight: 700,
                            borderRadius: "8px",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
